import { useMemo } from 'react'
import type { RSISeries } from '../components/charts/RSIChart'
import type { InspectionMode } from '../components/inspector/types'
import { calculateRSI } from '../lib/analysis-utils'
import type { UpstoxCandle } from '../services/candle-service'

interface UseRsiSeriesProps {
    localHistory: any[]
    mode: InspectionMode
    selectedSingleStrike: number
    selectedCeStrike: number
    selectedPeStrike: number
    selectedCustomCeStrikes: number[]
    selectedCustomPeStrikes: number[]
    symbol: string
    expiry: string | undefined
    rsiTimeFrame: number
    rsiDays?: number
    ceCandles?: UpstoxCandle[]
    peCandles?: UpstoxCandle[]
    indexCandles?: UpstoxCandle[]
}

export const useRsiSeries = ({
    localHistory,
    mode,
    selectedSingleStrike,
    selectedCeStrike,
    selectedPeStrike,
    selectedCustomCeStrikes,
    selectedCustomPeStrikes,
    symbol,
    expiry,
    rsiTimeFrame,
    rsiDays = 1,
    ceCandles = [],
    peCandles = [],
    indexCandles = [],
}: UseRsiSeriesProps): RSISeries[] => {
    return useMemo(() => {
        const getEpoch = (obj: any) =>
            typeof obj.timestamp === 'number' && obj.timestamp > 0
                ? obj.timestamp
                : new Date(obj.timestampIST || obj.timestamp).getTime()

        // 1. Target Instruments (Strikes/Types) from Mode
        const targets: { strike: number; type: 'CE' | 'PE' | 'INDEX'; label: string; data?: UpstoxCandle[] }[] = []

        // Add Index Target
        if (indexCandles && indexCandles.length > 0) {
            targets.push({
                strike: 0,
                type: 'INDEX',
                label: symbol,
                data: indexCandles,
            })
        } else if (localHistory && localHistory.length > 0) {
            targets.push({
                strike: 0,
                type: 'INDEX',
                label: symbol,
            })
        }

        if (mode === 'Single' && selectedSingleStrike) {
            targets.push({
                strike: selectedSingleStrike,
                type: 'CE',
                label: `${selectedSingleStrike} CE`,
                data: ceCandles,
            })
            targets.push({
                strike: selectedSingleStrike,
                type: 'PE',
                label: `${selectedSingleStrike} PE`,
                data: peCandles,
            })
        } else if (mode === 'Multiple') {
            if (selectedCeStrike)
                targets.push({
                    strike: selectedCeStrike,
                    type: 'CE',
                    label: `${selectedCeStrike} CE`,
                    data: ceCandles,
                })
            if (selectedPeStrike)
                targets.push({
                    strike: selectedPeStrike,
                    type: 'PE',
                    label: `${selectedPeStrike} PE`,
                    data: peCandles,
                })
        } else if (mode === 'Custom') {
            // Custom Mode falls back to localHistory (Snapshot Analysis)
            // ... existing logic
            // We can't efficiently fetch candles for N random strikes without heavy API usage
            if (!localHistory || localHistory.length < 15) return []

            selectedCustomCeStrikes.forEach((s) => {
                targets.push({ strike: s, type: 'CE', label: `${s} CE` })
            })
            selectedCustomPeStrikes.forEach((s) => {
                targets.push({ strike: s, type: 'PE', label: `${s} PE` })
            })
        }

        if (targets.length === 0) return []

        const seriesList: RSISeries[] = []

        // Helper: Generate color
        const getColor = (_strike: number, type: string, idx: number) => {
            if (type === 'INDEX') return '#8b5cf6' // Vibrant violet/purple for Index RSI
            if (targets.length <= 3) {
                if (type === 'CE') return '#10b981'
                if (type === 'PE') return '#ef4444'
            }
            const hues = [
                '#3b82f6',
                '#8b5cf6',
                '#ec4899',
                '#f97316',
                '#eab308',
                '#22c55e',
                '#06b6d4',
                '#ef4444',
                '#eab308',
            ]
            return hues[idx % hues.length] || '#64748b'
        }

        targets.forEach((target, idx) => {
            const prices: number[] = []
            const times: number[] = []

            // A. High Fidelity Candle Data (Single/Multiple Mode)
            if (target.data && target.data.length > 14) {
                let processedCandles = target.data
                if (rsiTimeFrame > 1) {
                    const msPerFrame = rsiTimeFrame * 60 * 1000
                    const buckets = new Map<number, UpstoxCandle>()
                    target.data.forEach((c) => {
                        const t = new Date(c.timestamp).getTime()
                        const key = Math.floor(t / msPerFrame) * msPerFrame
                        buckets.set(key, c)
                    })
                    processedCandles = Array.from(buckets.entries())
                        .sort((a, b) => a[0] - b[0])
                        .map((e) => e[1])
                }

                if (processedCandles.length > 14) {
                    processedCandles.forEach((c) => {
                        const t = new Date(c.timestamp).getTime() / 1000
                        if (c.close && t > 0) {
                            prices.push(c.close)
                            times.push(t)
                        }
                    })
                }
            }

            // B. Fallback to Snapshot History (Custom Mode or insufficient candle data)
            if (prices.length < 15 && localHistory && localHistory.length > 14) {
                // ... Existing logic for localHistory ...
                const sortedHistory = [...localHistory]
                    .filter((item) => {
                        const t = getEpoch(item)
                        return !Number.isNaN(t) && t > 0
                    })
                    .sort((a, b) => getEpoch(a) - getEpoch(b))
                // Resample
                let processedHistory = sortedHistory
                if (rsiTimeFrame > 1) {
                    const buckets = new Map<number, any>()
                    const msPerFrame = rsiTimeFrame * 60 * 1000
                    sortedHistory.forEach((snap) => {
                        const time = getEpoch(snap)
                        const bucketKey = Math.floor(time / msPerFrame) * msPerFrame
                        buckets.set(bucketKey, snap)
                    })
                    processedHistory = Array.from(buckets.keys())
                        .sort((a, b) => a - b)
                        .map((k) => buckets.get(k))
                }

                processedHistory.forEach((snap) => {
                    if (target.type === 'INDEX') {
                        const price = snap.spotPrice || snap.indexLtp || 0
                        const timeVal = getEpoch(snap) / 1000
                        if (!Number.isNaN(price) && !Number.isNaN(timeVal) && timeVal > 0) {
                            prices.push(price)
                            times.push(timeVal)
                        }
                    } else {
                        // Check deeply for options data
                        let opts: any[] = []
                        if (snap.optionChain && Array.isArray(snap.optionChain)) opts = snap.optionChain
                        else if (snap.options && Array.isArray(snap.options)) opts = snap.options
                        else if (snap.optionsData && snap.optionsData[symbol]) {
                            const byExpiry = Object.values(snap.optionsData[symbol])[0]
                            if (Array.isArray(byExpiry)) opts = byExpiry
                        }

                        const opt = opts.find((o: any) => o.strike === target.strike)

                        if (opt) {
                            const price =
                                target.type === 'CE'
                                    ? opt.callPrice || opt.CE_LTP || opt.cePrice || opt.ce?.ltp || 0
                                    : opt.putPrice || opt.PE_LTP || opt.pePrice || opt.pe?.ltp || 0
                            const timeVal = getEpoch(snap) / 1000
                            if (!Number.isNaN(price) && price > 0 && !Number.isNaN(timeVal) && timeVal > 0) {
                                prices.push(price)
                                times.push(timeVal)
                            }
                        }
                    }
                })
            }

            if (prices.length > 14) {
                const rsiValues = calculateRSI(prices, 14)
                let data = []
                for (let i = 0; i < prices.length; i++) {
                    const t = times[i]
                    const v = rsiValues[i]
                    if (!Number.isNaN(v) && Number.isFinite(v)) {
                        data.push({ time: t, value: v })
                    }
                }

                if (rsiDays && rsiDays < 10 && data.length > 0) {
                    const getISTDayIndex = (tS: number) => Math.floor((tS * 1000 + 19800000) / 86400000)
                    const daysSet = new Set<number>()
                    data.forEach((d) => daysSet.add(getISTDayIndex(d.time as number)))

                    const sortedDays = Array.from(daysSet).sort((a, b) => b - a)
                    const allowedDays = new Set(sortedDays.slice(0, rsiDays))

                    data = data.filter((d) => allowedDays.has(getISTDayIndex(d.time as number)))
                }

                if (data.length > 0) {
                    seriesList.push({
                        name: target.label || 'Unknown',
                        color: getColor(target.strike, target.type, idx),
                        data: data as { time: number; value: number }[],
                    })
                }
            }
        })

        return seriesList
    }, [
        localHistory,
        rsiTimeFrame,
        rsiDays,
        mode,
        selectedSingleStrike,
        selectedCeStrike,
        selectedPeStrike,
        selectedCustomCeStrikes,
        selectedCustomPeStrikes,
        symbol,
        expiry,
        ceCandles,
        peCandles,
        indexCandles,
    ])
}
